/**
 * `crypto.randomUUID()` only exists in "secure contexts" (HTTPS or
 * localhost) per the spec — a shop opening this app over plain HTTP on its
 * local network (e.g. `http://192.168.x.x:5273`, which is how a single
 * till/shop deployment is commonly reached) gets `crypto.randomUUID is not
 * a function` and the whole screen crashes (seen on bill pay, which uses it
 * for client-generated payment-line ids and the idempotency `clientOpId`).
 *
 * `crypto.getRandomValues()` has no such restriction, so we build a v4 UUID
 * from it when `randomUUID` isn't available, and fall back to `Math.random`
 * only if `crypto` itself is entirely missing (very old browsers) — good
 * enough for client-side ids that just need to be unique, not cryptographically
 * unguessable.
 */
export function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last-resort fallback — not cryptographically random, but unique enough
  // for a client-side id/idempotency key.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
